/**
 * Account Component
 * 
 * Main account management page that displays user profile, password change,
 * MFA settings, and active sessions.
 * 
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5
 */

import React, { useState } from 'react';
import type { Session, User } from '../types/auth.types';
import { ErrorAlert } from './ErrorAlert';
import { PasswordChangeForm } from './PasswordChangeForm';
import { ProfileEditor } from './ProfileEditor';
import { SessionsList } from './SessionsList';
import { WebhooksTab } from './WebhooksTab';

export interface AccountProps {
  user: User;
  sessions?: Session[];
  currentSessionId?: string;
  onUpdateProfile: (updates: Partial<User>) => Promise<void>;
  onChangePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  onTerminateSession: (sessionId: string) => Promise<void>;
  onRefreshSessions?: () => Promise<void>;
  onEnableMFA?: () => void;
  onDisableMFA?: () => void;
  /** Whether MFA is required for the current platform (hides MFA tab when false) */
  mfaRequired?: boolean;
}

type ActiveTab = 'profile' | 'password' | 'mfa' | 'sessions' | 'webhooks';

export const Account: React.FC<AccountProps> = ({
  user,
  sessions = [],
  currentSessionId,
  onUpdateProfile,
  onChangePassword,
  onTerminateSession,
  onRefreshSessions,
  onEnableMFA,
  onDisableMFA,
  mfaRequired = true,
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('profile');
  const [errors, setErrors] = useState<string[]>([]);

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    backgroundColor: isActive ? '#fff' : '#f5f5f5',
    border: 'none',
    borderBottom: isActive ? '3px solid #3498db' : '3px solid transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? '#3498db' : '#666',
    transition: 'all 0.2s',
  });

  const sectionStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  const headingStyle: React.CSSProperties = {
    margin: '0 0 16px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#333',
  };

  const infoRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0',
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#666',
    fontSize: '14px',
  };

  const valueStyle: React.CSSProperties = {
    color: '#333',
    fontSize: '14px',
  };

  const badgeStyle = (status: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: status === 'active' ? '#d4edda' : '#f8d7da',
    color: status === 'active' ? '#155724' : '#721c24',
  });

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  };

  const dangerButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#e74c3c',
  };

  return (
    <div className="account-page">
      {errors.length > 0 && <ErrorAlert message={errors} onDismiss={() => setErrors([])} />}

      {/* Tab Navigation */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px 8px 0 0',
          marginBottom: '0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          gap: '0',
        }}
      >
        <button
          onClick={() => setActiveTab('profile')}
          style={tabStyle(activeTab === 'profile')}
        >
          Profile
        </button>
        <button
          onClick={() => setActiveTab('password')}
          style={tabStyle(activeTab === 'password')}
        >
          Password
        </button>
        <button
          onClick={() => setActiveTab('mfa')}
          style={tabStyle(activeTab === 'mfa')}
        >
          Security (MFA)
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          style={tabStyle(activeTab === 'sessions')}
        >
          Active Sessions
        </button>
        <button
          onClick={() => setActiveTab('webhooks')}
          style={tabStyle(activeTab === 'webhooks')}
        >
          Webhooks
        </button>
      </div>

      {/* Tab Content */}
      <div style={{ ...sectionStyle, borderRadius: '0 0 8px 8px' }}>
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div>
            <h2 style={headingStyle}>Profile Information</h2>
            
            <div style={{ marginBottom: '24px' }}>
              <div style={infoRowStyle}>
                <span style={labelStyle}>Email</span>
                <span style={valueStyle}>{user.email}</span>
              </div>
              <div style={infoRowStyle}>
                <span style={labelStyle}>Name</span>
                <span style={valueStyle}>{user.displayName}</span>
              </div>
              <div style={infoRowStyle}>
                <span style={labelStyle}>Phone</span>
                <span style={valueStyle}>{user.phoneNumber || 'Not provided'}</span>
              </div>
              <div style={infoRowStyle}>
                <span style={labelStyle}>User Type</span>
                <span style={valueStyle}>{user.userType}</span>
              </div>
              <div style={infoRowStyle}>
                <span style={labelStyle}>Status</span>
                <span style={badgeStyle(user.status)}>{user.status}</span>
              </div>
              <div style={infoRowStyle}>
                <span style={labelStyle}>Email Verified</span>
                <span style={valueStyle}>{user.emailVerified ? '✓ Yes' : '✗ No'}</span>
              </div>
              <div style={infoRowStyle}>
                <span style={labelStyle}>MFA Enabled</span>
                <span style={valueStyle}>{user.mfaEnabled ? '✓ Yes' : '✗ No'}</span>
              </div>
            </div>

            <h3 style={{ ...headingStyle, fontSize: '16px', marginTop: '24px' }}>
              Edit Profile
            </h3>
            <ProfileEditor user={user} onSubmit={onUpdateProfile} />
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div>
            <h2 style={headingStyle}>Change Password</h2>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
              Update your password to keep your account secure. Your new password must meet
              all security requirements.
            </p>
            <PasswordChangeForm onSubmit={onChangePassword} />
          </div>
        )}

        {/* MFA Tab */}
        {activeTab === 'mfa' && (
          <div>
            <h2 style={headingStyle}>Multi-Factor Authentication</h2>
            {!mfaRequired ? (
              <div>
                <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
                  Multi-factor authentication is optional for this platform. You can still
                  enable it for additional security if you wish.
                </p>
                <div style={{ ...infoRowStyle, borderBottom: 'none' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      MFA Status: {user.mfaEnabled ? (
                        <span style={{ color: '#27ae60' }}>✓ Enabled</span>
                      ) : (
                        <span style={{ color: '#999' }}>○ Not enabled (optional)</span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      {user.mfaEnabled
                        ? 'Your account is protected with multi-factor authentication'
                        : 'MFA is not required for this platform but can be enabled for extra security'}
                    </div>
                  </div>
                  <div>
                    {user.mfaEnabled ? (
                      onDisableMFA && (
                        <button onClick={onDisableMFA} style={dangerButtonStyle}>
                          Disable MFA
                        </button>
                      )
                    ) : (
                      onEnableMFA && (
                        <button onClick={onEnableMFA} style={buttonStyle}>
                          Enable MFA
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
                  Add an extra layer of security to your account by enabling multi-factor
                  authentication (MFA).
                </p>

                <div style={{ ...infoRowStyle, borderBottom: 'none' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      MFA Status: {user.mfaEnabled ? (
                        <span style={{ color: '#27ae60' }}>✓ Enabled</span>
                      ) : (
                        <span style={{ color: '#e74c3c' }}>✗ Disabled</span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      {user.mfaEnabled
                        ? 'Your account is protected with multi-factor authentication'
                        : 'Enable MFA to add an extra layer of security'}
                    </div>
                  </div>
                  <div>
                    {user.mfaEnabled ? (
                      onDisableMFA && (
                        <button onClick={onDisableMFA} style={dangerButtonStyle}>
                          Disable MFA
                        </button>
                      )
                    ) : (
                      onEnableMFA && (
                        <button onClick={onEnableMFA} style={buttonStyle}>
                          Enable MFA
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div>
            <h2 style={headingStyle}>Active Sessions</h2>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
              Manage your active sessions across all devices. You can terminate any session
              that you don't recognize.
            </p>
            <SessionsList
              sessions={sessions}
              currentSessionId={currentSessionId}
              onTerminateSession={onTerminateSession}
              onRefresh={onRefreshSessions}
            />
          </div>
        )}

        {/* Webhooks Tab */}
        {activeTab === 'webhooks' && (
          <div>
            <WebhooksTab />
          </div>
        )}
      </div>
    </div>
  );
};
